// Redesigned LeadsPanel with improved visuals and filter container box
import React, { useContext, useState, useEffect } from "react";
import { GlobalContext } from "../GlobalContext";
import axiosInstance from "../services/BaseUrl";
import { FiTrash2, FiEye } from "react-icons/fi";
import { DateRangePicker } from "react-date-range";
import { saveAs } from "file-saver";
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { FiFilter, FiCalendar, FiDownload, FiSearch } from "react-icons/fi";
import { FiMail } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';

export default function LeadsPanel() {
  const { leads, setLeads } = useContext(GlobalContext);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [leadsPerPage] = useState(10);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedRange, setSelectedRange] = useState([null, null]);

  useEffect(() => {
    const filtered = leads.filter((lead) => {
      const matchesType = filterType ? lead.lead_type === filterType : true;
      const matchesStatus = filterStatus ? lead.status === filterStatus : true;
      const matchesSearch = lead.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDateRange = selectedRange[0] && selectedRange[1]
        ? new Date(lead.timestamp) >= new Date(selectedRange[0]) && new Date(lead.timestamp) <= new Date(selectedRange[1])
        : true;
      return matchesType && matchesStatus && matchesSearch && matchesDateRange;
    });
    setFilteredLeads(filtered);
  }, [leads, filterType, filterStatus, searchTerm, selectedRange]);

  const indexOfLastLead = currentPage * leadsPerPage;
  const indexOfFirstLead = indexOfLastLead - leadsPerPage;
  const currentLeads = filteredLeads.slice(indexOfFirstLead, indexOfLastLead);
  const totalPages = Math.ceil(filteredLeads.length / leadsPerPage);

  const handleDateChange = (ranges) => {
    setSelectedRange([ranges.selection.startDate, ranges.selection.endDate]);
    setShowCalendar(false);
  };

const deleteLead = async (id) => {
  try {
    const res = await axiosInstance.delete(`/leads/${id}/`);
    if (res.status === 200) {
      setLeads((prev) => prev.filter((lead) => lead.id !== id));
      setLeadToDelete(null); // ✅ close modal after deletion
    } else {
      alert("Failed to delete lead.");
    }
  } catch (err) {
    alert("Failed to delete lead.");
  }
};

  const exportToCSV = () => {
    const csvRows = [["Full Name", "Contact Number", "Type", "Inquiry Link", "Date & Time"]];
    currentLeads.forEach((lead) => {
      csvRows.push([
        lead.full_name || "",
        lead.contact_number || "",
        lead.lead_type || "",
        lead.inquiry_link || "",
        new Date(lead.timestamp).toLocaleString()
      ]);
    });
    const csvData = new Blob([csvRows.map(row => row.join(",")).join("\n")], { type: "text/csv" });
    saveAs(csvData, "leads.csv");
  };

  return (
    <div className="px-6 py-22 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800 mb-1">Lead Management</h1>
      <p className="text-gray-600 mb-6 mt-4">Manage and track your leads efficiently</p>

      {/* Dashboard Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Leads', value: leads.length },
          { label: 'Email Leads', value: leads.filter(l => l.lead_type === 'email').length },
          { label: 'WhatsApp Leads', value: leads.filter(l => l.lead_type === 'whatsapp').length },
          { label: 'Phone Leads', value: leads.filter(l => l.lead_type === 'call').length },
        ].map((card, i) => (
          <div key={i} className="bg-white shadow rounded-xl p-5 border border-gray-100">
            <p className="text-gray-600 text-sm mb-1">{card.label}</p>
            <p className="text-2xl font-bold text-gray-900">{card.value}</p>
            <p className="text-green-600 text-xs mt-1">+2.5% from last week</p>
          </div>
        ))}
      </div>

 {/* Filter Box Container */}
<div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
  <div className="flex flex-wrap lg:flex-nowrap items-center gap-3">
    
    {/* Search Field with Icon */}
    <div className="flex items-center border border-gray-300 rounded-md px-3 py-2 flex-1 min-w-[250px]">
      <FiSearch className="text-gray-500 mr-2" />
      <input
        type="text"
        placeholder="Search by name or contact number..."
        className="text-sm w-full focus:outline-none"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
    </div>

    {/* Filter Type with Icon */}
    <div className="relative min-w-[140px]">
      <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
      <select
        value={filterType}
        onChange={(e) => setFilterType(e.target.value)}
        className="pl-8 border border-gray-300 px-3 py-2 rounded-md text-sm w-full "
      >
        <option value="">All Types</option>
        <option value="email">Email</option>
        <option value="whatsapp">WhatsApp</option>
        <option value="call">Phone</option>
      </select>
    </div>

    {/* Filter Status */}
    <div className="min-w-[140px]">
      <select
        value={filterStatus}
        onChange={(e) => setFilterStatus(e.target.value)}
        className="border border-gray-300 px-3 py-2 rounded-md text-sm w-full"
      >
        <option value="">All Status</option>
        <option value="New">New</option>
        <option value="Contacted">Contacted</option>
      </select>
    </div>

    {/* Date Range with Icon */}
    <div className="relative min-w-[140px]">
      <button
        onClick={() => setShowCalendar(!showCalendar)}
        className="border border-gray-300 px-4 py-2 rounded-md text-sm w-full flex items-center justify-center gap-2"
      >
        <FiCalendar />
        Date Range
      </button>
    </div>

    {/* Export CSV */}
    <div className="min-w-[140px]">
      <button
        onClick={exportToCSV}
        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium w-full flex items-center justify-center gap-2"
      >
        <FiDownload />
        Export CSV
      </button>
    </div>
  </div>

  {/* Calendar Picker */}
  {showCalendar && (
    <div className="z-50 mt-3">
      <DateRangePicker
        ranges={[{ startDate: selectedRange[0], endDate: selectedRange[1], key: 'selection' }]}
        onChange={handleDateChange}
      />
    </div>
  )}
</div>


      {/* Leads Table */}
      <div className="overflow-x-auto bg-white rounded-xl shadow border border-gray-100">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              {['Full Name', 'Contact Number', 'Type', 'Status', 'Inquiry Link', 'Date & Time', 'Actions'].map((h, i) => (
  <th key={i} className="px-6 py-3 font-semibold">{h}</th>
))}
            </tr>
          </thead>
          <tbody>
            {currentLeads.map((lead) => (
              <tr key={lead.id} className="border-t border-gray-200 hover:bg-gray-50">
                <td className="px-6 py-3">{lead.full_name || 'N/A'}</td>
                <td className="px-6 py-3">{lead.contact_number}</td>
                <td className="px-6 py-3">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full
  ${lead.lead_type === 'email' ? 'bg-blue-100 text-blue-600' :
    lead.lead_type === 'whatsapp' ? 'bg-green-100 text-green-600' :
    'bg-purple-100 text-purple-600'}`}>

  {lead.lead_type === 'email' && (
    <>
      <FiMail className="text-blue-600 text-sm" />
      Email
    </>
  )}

  {lead.lead_type === 'whatsapp' && (
    <>
      <FaWhatsapp className="text-green-600 text-sm" />
      WhatsApp
    </>
  )}

  {lead.lead_type !== 'email' && lead.lead_type !== 'whatsapp' && (
    <>
      {/* fallback icon or text */}
      {lead.lead_type}
    </>
  )}
</span>                </td>
                <td className="px-6 py-3">
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${lead.status === 'New' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>{lead.status}</span>
                </td>
               <td className="px-6 py-3">
  <a href={lead.inquiry_link} target="_blank" rel="noreferrer" className="text-blue-600 underline hover:text-blue-800">
    View Link
  </a>
</td>
<td className="px-6 py-3">{new Date(lead.timestamp).toLocaleString()}</td>
<td className="px-6 py-3 flex gap-2">
  <button onClick={() => setLeadToDelete(lead.id)} className="inline-flex items-center px-3 py-1 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50"><FiTrash2 className="mr-1"/> Delete</button>
</td>

              </tr>
            ))}
            {currentLeads.length === 0 && (
              <tr><td colSpan="6" className="text-center py-6 text-gray-500">No leads found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-6 flex justify-center gap-2">
        {[...Array(totalPages)].map((_, i) => (
          <button key={i} onClick={() => setCurrentPage(i + 1)} className={`px-3 py-1 rounded-md text-sm ${currentPage === i + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}>{i + 1}</button>
        ))}
      </div>

      {/* Delete Modal */}
      {leadToDelete !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p className="mb-6 text-gray-600">Are you sure you want to delete this lead?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setLeadToDelete(null)} className="px-4 py-2 rounded border border-gray-300">Cancel</button>
              <button onClick={() => deleteLead(leadToDelete)} className="px-4 py-2 rounded bg-red-600 text-white">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}